import React, { useRef, useState } from 'react';
import { Problem, Settings } from '../types/problem';
import { cancelSubscription, importProblems } from '../hooks/useApi';
import { useAuth, PLAN_NAMES } from '../auth';
import { Link, useLocation } from '../router';
import { formatDate, plural } from '../utils/format';
import Icon from './Icon';

interface SettingsPanelProps {
  settings: Settings;
  problems: Problem[];
  onSave: (settings: Settings) => Promise<void>;
  onImported: () => void;
}

const PRESETS = [
  { value: 7, title: 'Intensive', desc: 'Every problem at least weekly' },
  { value: 14, title: 'Balanced', desc: 'Every two weeks' },
  { value: 21, title: 'Relaxed', desc: 'Every three weeks' },
  { value: 30, title: 'Light', desc: 'Roughly monthly' },
];

function PlanCard() {
  const { me, setMe } = useAuth();
  const { search } = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const user = me!.user!;
  const usage = me!.usage!;
  const paid = user.plan !== 'free';
  const planName = PLAN_NAMES[user.plan];
  const cancelling = user.subscriptionStatus === 'cancelling' || user.subscriptionStatus === 'cancelled';

  async function handleCancel() {
    if (!window.confirm(`Cancel ${planName}? You keep it until the end of the period you paid for.`)) return;
    setBusy(true);
    setError('');
    try {
      setMe(await cancelSubscription());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not cancel');
    } finally {
      setBusy(false);
    }
  }

  const pct = usage.limit ? Math.min(100, (usage.used / usage.limit) * 100) : 0;

  return (
    <section className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Plan & usage</div>
          <div className="card-desc">Signed in as {user.email}</div>
        </div>
        <span className={`badge ${paid ? 'badge-accent' : ''}`}>{planName}</span>
      </div>
      <div className="card-body">
        {search.get('upgraded') && paid && (
          <div className="alert" style={{ marginBottom: 16 }}>
            <Icon name="check" />
            <div>
              <strong>Welcome to {planName}.</strong>{' '}
              {usage.unlimited ? 'AI grading is now unlimited.' : `You now get ${plural(usage.limit, 'AI grade')} a month.`}
            </div>
          </div>
        )}
        <div className="field-label">AI grading this month</div>
        {usage.unlimited ? (
          <p className="field-help" style={{ margin: 0 }}>Unlimited with {planName}. {plural(usage.used, 'answer')} graded so far.</p>
        ) : (
          <>
            <div className={`usage-meter ${usage.used >= usage.limit ? 'full' : ''}`}><div style={{ width: `${pct}%` }} /></div>
            <p className="field-help" style={{ margin: 0 }}>
              {usage.used} of {usage.limit} {paid ? '' : 'free '}grades used. Resets on the 1st of each month. Self-graded reviews are always unlimited.
            </p>
          </>
        )}
        {paid && user.paidUntil && (
          <p className="field-help" style={{ margin: '12px 0 0' }}>
            {cancelling ? `${planName} ends` : 'Renews'} on {formatDate(new Date(user.paidUntil).getTime())}.
            {!cancelling && ' To switch plans, cancel this one, then pick a new plan on the pricing page.'}
          </p>
        )}
      </div>
      <div className="card-footer">
        {error ? <span className="error-text">{error}</span> : (
          <span className="next-hint">
            {user.plan === 'pro' ? 'Thanks for supporting CodeReviser.'
              : user.plan === 'plus' ? 'Pro removes the monthly AI grading limit.'
              : 'Plus and Pro give you more AI grading each month.'}
          </span>
        )}
        <span className="spacer" />
        {cancelling && (
          <Link to="/pricing" className="btn btn-secondary" style={{ textDecoration: 'none' }}>See plans</Link>
        )}
        {paid ? (
          !cancelling && (
            <button className="btn btn-ghost" onClick={handleCancel} disabled={busy}>
              {busy && <span className="spinner" />} Cancel subscription
            </button>
          )
        ) : (
          <Link to="/pricing" className="btn btn-primary" style={{ textDecoration: 'none' }}>
            <Icon name="star" size={14} /> See plans
          </Link>
        )}
      </div>
    </section>
  );
}

function DataCard({ problems, onImported }: { problems: Problem[]; onImported: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  function handleExport() {
    const map: Record<string, Problem> = {};
    problems.forEach(p => { map[p.id] = p; });
    const blob = new Blob([JSON.stringify(map, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `codereviser-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      let data = JSON.parse(await file.text());
      if (Array.isArray(data)) data = Object.fromEntries(data.filter(p => p?.slug).map(p => [p.slug, p]));
      const res = await importProblems(data);
      setStatus({ tone: 'ok', text: `Imported ${plural(res.imported, 'problem')}${res.skipped ? `, skipped ${res.skipped} invalid` : ''}.` });
      onImported();
    } catch (err) {
      setStatus({ tone: 'error', text: err instanceof SyntaxError ? 'That file is not valid JSON.' : (err as Error).message });
    }
  }

  return (
    <section className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Your data</div>
          <div className="card-desc">Back up your problems and review history, or restore a backup (including files from the old self-hosted version).</div>
        </div>
      </div>
      <div className="card-footer">
        {status ? (
          <span className={status.tone === 'error' ? 'error-text' : 'next-hint'}>{status.text}</span>
        ) : (
          <span className="next-hint">{plural(problems.length, 'problem')} saved</span>
        )}
        <span className="spacer" />
        <input ref={fileRef} type="file" accept=".json,application/json" hidden onChange={handleImport} />
        <button className="btn btn-secondary" onClick={() => fileRef.current?.click()}>Import</button>
        <button className="btn btn-secondary" onClick={handleExport} disabled={problems.length === 0}>
          <Icon name="download" size={14} /> Export
        </button>
      </div>
    </section>
  );
}

export default function SettingsPanel({ settings, problems, onSave, onImported }: SettingsPanelProps) {
  const [cycleDays, setCycleDays] = useState(settings.revisionCycleDays);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await onSave({ revisionCycleDays: cycleDays });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save settings');
    } finally {
      setSaving(false);
    }
  }

  const totalProblems = problems.length;
  const changed = cycleDays !== settings.revisionCycleDays;
  const perDay = totalProblems ? Math.ceil(totalProblems / cycleDays) : 0;

  return (
    <div className="page-narrow">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Your plan, review schedule and data.</p>
        </div>
      </div>

      <PlanCard />

      <section className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Revision cycle</div>
            <div className="card-desc">
              The longest gap between reviews. Shorter cycles mean more problems per day.
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="option-grid">
            {PRESETS.map(p => (
              <button
                key={p.value}
                className={`option ${cycleDays === p.value ? 'active' : ''}`}
                aria-pressed={cycleDays === p.value}
                onClick={() => setCycleDays(p.value)}
              >
                <div className="option-title">{p.title} · {p.value} days</div>
                <div className="option-desc">{p.desc}</div>
              </button>
            ))}
          </div>

          <label className="inline-field">
            Custom
            <input
              className="input"
              type="number"
              min={1}
              max={90}
              value={cycleDays}
              onChange={e => setCycleDays(Math.max(1, Math.min(90, parseInt(e.target.value) || 1)))}
            />
            days
          </label>
        </div>
        <div className="card-footer">
          {error ? (
            <span className="error-text">{error}</span>
          ) : (
            <span className="next-hint">
              With {plural(totalProblems, 'problem')}, that's about {plural(perDay, 'problem')} a day.
            </span>
          )}
          <span className="spacer" />
          <button className="btn btn-primary" onClick={handleSave} disabled={!changed || saving}>
            {saving && <span className="spinner" />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </section>

      <DataCard problems={problems} onImported={onImported} />
    </div>
  );
}
